import React, { useMemo, useEffect, useRef, useState } from "react";
import { getNameByLocale } from "../../utils/language";
import { isEmpty, isString, findByPropValueFn } from "../../utils/helpers";
import Icon from "../Icon";
import { ReactSelectOptionObject } from "../InputSelectCustom";
import InputSelect from "../InputSelect";
import "./LanguageSwitcher.css";

export interface LanguageSwitcherProps {
  languages: string[];
  currentLanguage: string;
  stepForm?: number;
  id?: string;
  icon?: React.ReactNode;
  label?: React.ReactNode;
  sortOptions?: boolean;
  onChange: React.ChangeEventHandler;
}

const LANG_NAME_LENGTHS: { [langValue: string]: string } = {};

/**
 * All this jiggery-pokery is to constrain the select input element to
 * be as wide as its label (including some padding for the arrow).
 */
function getLangNameLength(
  currentLanguageOption: ReactSelectOptionObject,
  elem: HTMLElement
): string | null {
  const optionValue = isString(currentLanguageOption.value)
    ? String(currentLanguageOption.value)
    : "";

  if (!optionValue) {
    return null;
  }

  if (LANG_NAME_LENGTHS.hasOwnProperty(optionValue)) {
    return LANG_NAME_LENGTHS[optionValue];
  } else {
    // Check out this sexy code
    const styles = window.getComputedStyle(elem, null);
    const c = document.createElement("div");
    c.classList.add("-helper-calculate-width");
    const s = document.createElement("span");
    s.textContent = currentLanguageOption.label;
    s.style.display = "inline-block";
    s.style.fontFamily = styles.fontFamily;
    s.style.fontSize = styles.fontSize;
    s.style.letterSpacing = styles.letterSpacing;
    s.style.textTransform = styles.textTransform;
    c.appendChild(s);
    document.body.appendChild(c);
    const labelWidth = s.clientWidth + 5;
    document.body.removeChild(c);
    LANG_NAME_LENGTHS[optionValue] = `calc(${labelWidth}px + 2em)`; // don't forget to add space for the `input-select-arrow` icon
    return LANG_NAME_LENGTHS[optionValue];
  }
}

export default function LanguageSwitcher({
  languages,
  currentLanguage,
  id = "language-switcher",
  icon = "language",
  label,
  sortOptions = true,
  stepForm,
  onChange
}: LanguageSwitcherProps) {
  const inputSelectRef = useRef<HTMLSelectElement>(null);

  const [currentLang, setCurrentLang] = useState(currentLanguage);

  // Memoized
  const options: ReactSelectOptionObject[] = useMemo(
    () =>
      // Ensure that if language is not set that this doesn't break the app
      !isEmpty(languages)
        ? languages
            .map(lang => {
              const label = getNameByLocale(lang);
              if (label) {
                return {
                  label,
                  value: lang
                };
              }
              return false;
            })
            .filter(Boolean)
        : [],
    [languages, currentLang]
  ) as ReactSelectOptionObject[];

  const _icon = isString(icon) ? <Icon name={String(icon)} /> : icon;

  // Ensure the width of the input is relative to the width of the selected option
  useEffect(() => {
    const currentLanguageOption = options.find(
      findByPropValueFn("value", currentLang)
    );
    if (currentLanguageOption && inputSelectRef.current) {
      const langNameLength = getLangNameLength(
        currentLanguageOption,
        inputSelectRef.current
      );
      inputSelectRef.current.style.width = langNameLength;
    }
  }, [id, currentLang]);

  useEffect(() => {
    if (!options.some(option => option.value === currentLanguage)) {
      const possibleLanguages = options.filter(
        option =>
          (option.value || "").toString().split("-")[0] === currentLanguage
      );
      if (possibleLanguages[0]) {
        // @ts-ignore
        setCurrentLang(possibleLanguages[0].value.toString());
      }
    } else {
      setCurrentLang(currentLanguage);
    }
  }, [options, currentLanguage]);

  return (
    <label htmlFor={id} className="language-switcher">
      {!isEmpty(label) && (
        <span className="language-switcher-label">{label}</span>
      )}
      {!isEmpty(icon) && (
        <span className="language-switcher-icon">{_icon}</span>
      )}
      <InputSelect
        id={id}
        value={currentLang}
        options={options}
        onChange={onChange}
        ref={inputSelectRef}
        sortOptionsAlphabetically={sortOptions}
        disabled={stepForm === 1 || stepForm === 3}
      />
    </label>
  );
}
